import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * InfoBoxHeader - Reusable component for page headers with icon, title and breadcrumbs
 * @param {Object} props
 * @param {string} props.title - The page title
 * @param {string|React.ReactNode} props.icon - The icon to display
 * @param {Array} props.breadcrumbs - Array of breadcrumb items [{label, path, active}]
 * @param {React.ReactNode} props.rightContent - Optional content to display on the right side
 * @param {string} props.iconBgColor - Background color for the icon container
 * @param {boolean} props.showIcon - Whether to show the icon (default: true)
 */
const InfoBoxHeader = ({ 
  title, 
  icon, 
  breadcrumbs = [], 
  rightContent = null,
  iconBgColor = '#0364F3',
  showIcon = true // New prop with default value true
}) => {
  const navigate = useNavigate();

  // Handle navigation for breadcrumb clicks
  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <>
      <div className="info-box flex flex-col px-3 sm:px-6 max-w-screen bg-[#fbfbfd]">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full sm:pt-4 py-4">
          <div className="flex align-stretch mb-0">
            {/* Icon Container - only render if showIcon is true */}
            {showIcon && icon && (
              <div 
                className="w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] rounded-[30px] flex items-center justify-center mr-4 sm:mr-6"
                style={{ backgroundColor: iconBgColor }}
              >
                <img 
                  src={icon} 
                  alt={title} 
                  className="w-6 h-6 sm:w-auto sm:h-auto" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/24?text=I";
                  }}
                />
              </div>
            )}
            
            {/* Title and Breadcrumbs */}
            <div className="flex flex-col justify-center gap-1 h-[50px] sm:h-[60px]">
              <div className="text-xl sm:text-2xl lg:text-[28px] font-[700] text-neutral-900">
                {title}
              </div>
              
              {/* Breadcrumbs */}
              <div className="self-stretch inline-flex flex-wrap items-center gap-1">
                {breadcrumbs.map((item, index) => (
                  <React.Fragment key={index}>
                    {/* Add separator (except for first item) */}
                    {index > 0 && (
                      <div className="text-zinc-800 text-xs sm:text-sm font-normal flex items-center">
                        <span className="mx-1">&gt;</span>
                      </div>
                    )}
                    
                    {/* Breadcrumb item */}
                    <div className={`${index === 0 ? 'text-blue-600' : 'text-zinc-800'} text-xs sm:text-sm font-normal flex items-center`}>
                      {item.active ? (
                        <span className="cursor-default">{item.label}</span>
                      ) : (
                        <button
                          onClick={() => handleNavigation(item.path)}
                          className="hover:underline"
                        >
                          {item.label}
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          
          {/* Optional Right Content */}
          {rightContent && (
            <div className="w-full sm:w-auto">
              {rightContent}
            </div>
          )}
        </div>
      </div>
      
      {/* Divider */}
      <div className="w-full max-w-12xl h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200" />
    </>
  );
};

export default InfoBoxHeader;